import {Component, DestroyRef, ElementRef, inject, OnInit, AfterViewInit, OnDestroy, ViewChildren, QueryList} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {ConfigDetailComponent} from "../../../shared/components/config-detail/config-detail.component";
import {DetailHeaderComponent} from "../../../shared/components/config-detail/detail-header/detail-header.component";
import {DetailBodyComponent} from "../../../shared/components/config-detail/detail-body/detail-body.component";
import {DiffEditorComponent, DiffEditorModel, EditorComponent} from "ngx-monaco-editor-v2";
import {NgOptimizedImage, UpperCasePipe} from "@angular/common";
import {TranslatePipe, TranslateService} from "@ngx-translate/core";
import {ConfigurationService} from "../../../shared/services/configuration.service";
import {FormsModule} from "@angular/forms";
import jsonata from "jsonata";
import {AppSettingsDataService} from "../../../app-settings/services/app-settings-data.service";
import {
    FindAllByApplicationNameServiceModel,
    FindApplicationSettingsCountServiceModel
} from "../../../app-settings/services/models/app-settings-service.model";
import {SnackbarService} from "../../../shared/services/snackbar.service";
import {RefreshService} from "../../../shared/services/refresh.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {
    DetailHeaderActionComponent
} from "../../../shared/components/config-detail/detail-header-action/detail-header-action.component";
import {JsonataExpressionsResizeService} from "../services/jsonata-expressions-resize.service";
import {AppSettingsAud} from "../../../app-settings/components/models/app-settings.model";
import {JsonataExpressionsDataService} from "../services/jsonata-expressions-data.service";
import {JsonataExpressionsService} from "../services/jsonata-expressions-service.model";
import {CardModule} from "primeng/card";
import {ButtonModule} from "primeng/button";
import {SelectModule} from "primeng/select";
import {ToggleSwitch} from "primeng/toggleswitch";

@Component({
    selector: 'app-jsonata-expressions',
    imports: [
        ConfigDetailComponent,
        DetailHeaderComponent,
        DetailBodyComponent,
        CardModule,
        SelectModule,
        EditorComponent,
        ButtonModule,
        NgOptimizedImage,
        TranslatePipe,
        UpperCasePipe,
        FormsModule,
        DetailHeaderActionComponent,
        ToggleSwitch,
        DiffEditorComponent
    ],
    templateUrl: './jsonata-expressions.component.html',
    styleUrl: './jsonata-expressions.component.scss',
})
export class JsonataExpressionsComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('resizeHandle', {read: ElementRef}) resizeHandles!: QueryList<ElementRef>;
    @ViewChildren('editorPanel', {read: ElementRef}) editorPanels!: QueryList<ElementRef>;

    protected readonly ConfigRouteEnum = ConfigRouteEnum;
    private readonly config = inject(ConfigurationService).configuration;
    private readonly translate = inject(TranslateService);
    private readonly appSettingsService = inject(AppSettingsDataService);
    private readonly snackBar = inject(SnackbarService);
    private readonly refreshService = inject(RefreshService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly resizeService = inject(JsonataExpressionsResizeService);
    private readonly jsonataExpressionsDataService = inject(JsonataExpressionsDataService);

    logoUrl: string | null = this.config.app.defaultLogo;
    wordWrapEnabled: boolean = false;

    jsonEditorOptions = {
        language: 'json',
        automaticLayout: true,
        minimap: {enabled: false},
        wordWrap: 'off',
    };

    jsonataEditorOptions = {
        language: 'jsonata',
        automaticLayout: true,
        minimap: {enabled: false},
        wordWrap: 'off',
    };

    outputEditorOptions = {
        language: 'json',
        readOnly: true,
        automaticLayout: true,
        minimap: {enabled: false},
        wordWrap: 'off',
    };

    options = {
        readOnly: true,
        automaticLayout: true,
        minimap: {enabled: false},
        wordWrap: 'off',
        renderSideBySide: true,
        useInlineViewWhenSpaceIsLimited: false,
        ignoreTrimWhitespace: false,
        originalEditable: false,
    };
    originalModel: DiffEditorModel = {
        code: '',
        language: 'jsonata'
    };
    modifiedModel: DiffEditorModel = {
        code: '',
        language: 'jsonata'
    };

    jsonData: string = '';
    jsonataData: string = '';
    outputData: string = '';

    selectedApplicationSetting: string | undefined;
    selectedApplicationSettingId: number | undefined;
    selectedJsonataExpression: JsonataExpressionsService | undefined;

    revisions: AppSettingsAud[] = [];
    selectedRevision: AppSettingsAud | undefined;

    isComparisonActive: boolean = false;
    isRevisionComparing: boolean = false;

    applicationSettings: FindApplicationSettingsCountServiceModel[] = [];
    applicationSettingIds: FindAllByApplicationNameServiceModel[] = [];
    jsonataSettings: JsonataExpressionsService[] = [];

    get applicationSettingOptions() {
        return [
            {data: undefined, label: this.translate.instant('jsonata.noSelection')},
            ...this.applicationSettings.map(s => ({data: s.applicationName, label: s.applicationName})),
        ];
    }

    get applicationSettingIdOptions() {
        return [
            {data: undefined, label: this.translate.instant('jsonata.noSelection')},
            ...this.applicationSettingIds.map(s => ({data: s.id, label: '#' + s.id})),
        ];
    }

    get jsonataSettingOptions() {
        return [
            {data: undefined, label: this.translate.instant('jsonata.noSelection')},
            ...this.jsonataSettings.map(s => ({data: s, label: s.identification + ' #' + s.id})),
        ];
    }

    get revisionOptions() {
        return [
            {data: undefined, label: this.translate.instant('appSettings.compareRev')},
            ...this.revisions.map(r => ({data: r, label: 'REV ' + r.rev})),
        ];
    }

    get revisionOptionsStop() {
        return [
            {data: undefined, label: this.translate.instant('query.stop')},
            ...this.revisions.map(r => ({data: r, label: 'REV ' + r.rev})),
        ];
    }

    ngOnInit() {
        this.loadApplicationSettings();
        this.loadJsonataSettings();
        void this.evaluateJsonata();

        this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.setSelectedJsonataFromRoute();
        });

        this.refreshService.refresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.selectedApplicationSetting = undefined;
            this.selectedApplicationSettingId = undefined;
            this.selectedJsonataExpression = undefined;
            this.applicationSettingIds = [];
            this.jsonData = '';
            this.jsonataData = '';
            this.outputData = '';
            this.loadApplicationSettings();
            this.loadJsonataSettings();
        });
    }

    ngAfterViewInit() {
        setTimeout(() => {
            this.resizeService.initializeResizeHandles(this.resizeHandles);
            this.resizeService.initializeWindowResizeListener(this.editorPanels);
        }, 0);
    }

    ngOnDestroy() {
        this.resizeService.cleanup();
    }

    private loadApplicationSettings() {
        this.appSettingsService.findApplicationSettingsCount().subscribe(settings => {
            this.applicationSettings = settings
                .filter(setting => !setting.applicationName.endsWith('Lang') && setting.applicationName !== 'JSONataExpressions')
                .sort((a, b) => a.applicationName.localeCompare(b.applicationName));
        });
    }

    private loadJsonataSettings() {
        this.jsonataExpressionsDataService.getJsonata().subscribe(settings => {
            settings.sort((a, b) => a.identification.localeCompare(b.identification));
            this.jsonataSettings = settings;
            this.setSelectedJsonataFromRoute();
        })
    }

    private setSelectedJsonataFromRoute() {
        const id = this.route.snapshot.paramMap.get('id');
        this.selectedJsonataExpression = this.jsonataSettings.find(s => s.id === Number(id));
        this.jsonataData = this.selectedJsonataExpression?.applicationSettings ?? '';
        void this.evaluateJsonata();
        this.loadRevisions();
    }

    async evaluateJsonata() {
        if (!this.jsonData) {
            this.outputData = this.translate.instant('jsonata.enterOrSelectJSON');
            return;
        }

        if (!this.jsonataData) {
            this.outputData = this.translate.instant('jsonata.enterOrSelectExpression');
            return;
        }

        try {
            const data = JSON.parse(this.jsonData);
            const expression = jsonata(this.jsonataData);
            const result = await expression.evaluate(data);
            if (result === undefined) {
                this.outputData = this.translate.instant('jsonata.noMatch');
            } else {
                this.outputData = JSON.stringify(result, null, 2);
            }
        } catch (e: any) {
            this.outputData = e.message;
        }
    }

    onApplicationSettingChange(applicationName: string | undefined) {
        this.selectedApplicationSettingId = undefined;
        this.applicationSettingIds = [];
        this.jsonData = '';
        void this.evaluateJsonata();
        if (applicationName) {
            this.appSettingsService.findAllByApplicationName(applicationName).subscribe(ids => {
                this.applicationSettingIds = ids.sort((a, b) => a.id - b.id);
            });
        }
    }

    onIdChange(id: number | undefined) {
        if (id) {
            const selectedSetting = this.applicationSettingIds.find(setting => setting.id === id);
            if (selectedSetting) {
                try {
                    this.jsonData = JSON.stringify(JSON.parse(selectedSetting.applicationSettings), null, 2);
                } catch (e) {
                    console.warn('Application setting contains invalid JSON:', selectedSetting.applicationSettings, e);
                    this.jsonData = selectedSetting.applicationSettings;
                }
                void this.evaluateJsonata();
            }
        } else {
            this.jsonData = '';
            void this.evaluateJsonata();
        }
    }

    onJsonataChange(setting: JsonataExpressionsService | undefined) {
        const id = setting?.id ?? 'new';
        this.router.navigate([ConfigRouteEnum.JSONATA, id]);
    }

    onWordWrapChange() {
        const newWordWrap = this.wordWrapEnabled ? 'on' : 'off';
        this.jsonEditorOptions = {
            ...this.jsonEditorOptions,
            wordWrap: newWordWrap
        };
        this.jsonataEditorOptions = {
            ...this.jsonataEditorOptions,
            wordWrap: newWordWrap
        };
        this.outputEditorOptions = {
            ...this.outputEditorOptions,
            wordWrap: newWordWrap
        };
        this.options = {
            ...this.options,
            wordWrap: newWordWrap
        };
    }

    saveJsonata() {
        const identification = (this.selectedApplicationSetting && this.selectedApplicationSettingId)
            ? this.selectedApplicationSetting + '_' + this.selectedApplicationSettingId
            : "JSONata_Expressions";

        const tenantName = (this.selectedApplicationSetting && this.selectedApplicationSettingId)
            ? this.selectedApplicationSetting
            : "JSONata_Expressions";

        const setting: JsonataExpressionsService = {
            id: this.selectedJsonataExpression?.id,
            applicationName: "JSONataExpressions",
            identification: identification,
            tenantName: tenantName,
            applicationSettings: this.jsonataData,
            active: true,
            participantId: '',
            participantPartnerId: '',
            relationType: '',
            applicationType: ''
        };

        const saveRequest = this.selectedJsonataExpression?.id
            ? this.jsonataExpressionsDataService.patchJsonata(setting, this.selectedJsonataExpression.id)
            : this.jsonataExpressionsDataService.postJsonata(setting);

        saveRequest.subscribe({
            next: (result) => {
                this.selectedJsonataExpression = result;
                this.loadJsonataSettings();
                this.router.navigate([ConfigRouteEnum.JSONATA, result.id]);
                this.snackBar.Success(this.translate.instant('snackbar.jsonata.save', {
                    identification: identification,
                    id: result.id,
                }));
            },
            error: (error) => {
                console.error(error);
                this.snackBar.Error(this.translate.instant('snackbar.save_error'));
            }
        });
    }

    loadRevisions() {
        const applicationName = this.selectedJsonataExpression?.applicationName;
        const tenantName = this.selectedJsonataExpression?.tenantName;
        const identification = this.selectedJsonataExpression?.identification;

        if (!applicationName || !tenantName || !identification) {
            return;
        }

        this.appSettingsService.getRevisionByApplicationNameAndIdentificationAndTenantName(applicationName, tenantName, identification).subscribe({
            next: (setting) => {
                this.revisions = setting.sort((a, b) => (b.rev ?? 0) - (a.rev ?? 0));
            },
            error: (error) => {
                console.error(error);
            }
        });
    }

    onRevisionSelected(revision: AppSettingsAud | undefined) {
        this.selectedRevision = revision;
        if (this.selectedRevision) {
            this.isRevisionComparing = true;
            this.isComparisonActive = true;
            this.originalModel = {
                code: this.jsonataData,
                language: 'jsonata',
            };

            this.modifiedModel = {
                code: this.selectedRevision?.applicationSettings ?? '',
                language: 'jsonata',
            };
        } else {
            this.stopComparing();
        }
    }

    stopComparing() {
        this.isComparisonActive = false;
        this.isRevisionComparing = false;
        this.selectedRevision = undefined;
    }

    replaceOriginalCode() {
        const modifiedCode = this.modifiedModel.code;
        if (!modifiedCode) {
            return;
        }

        this.stopComparing();
        this.jsonataData = modifiedCode;
        void this.evaluateJsonata();
    }
}
